// © Microsoft Corporation. All rights reserved.

import { ILoggingService } from "clientcore-infrastructure-analytics/ILoggingService";
import { Logger, logger } from "./Logger";
import { getQueryParameterByName } from "./Util";
import { canUseDOM } from "@msnews/isomorphic-util";

declare global {
    interface Window {
        webpackRequire: any;
    }
}

declare let __webpack_require__: any;

/**
 * Logger that simply logs to console with minimum dependencies.
 * Used for bootstrap/inline script where we don't need any logging nor its dependencies.
 */
export class LiteLogger implements Logger {

    /** Backing field for the isDebug function */
    private isDebugEnabled: boolean;

    /**
     * The constructor
     */
    public constructor() {
        if (canUseDOM()) {
            this.isDebugEnabled = ((getQueryParameterByName(window.location.href, "debug") || "").toLowerCase() in { 1: 1, true: 1 });

            // On debug mode, make webpack_require available as global function
            // so any code in packages could be triggered from console. Handy for debugging.
            if (this.isDebugEnabled && !global["TEST_ENV"]) {
                window.webpackRequire = __webpack_require__;
            }
        } else {
            this.isDebugEnabled = false; // isDebugEnabled should be set explictly using the setDebug method
        }
    }

    /**
     * Returns the logging service
     * @returns {ILoggingService}
     */
    public getLoggingService(): ILoggingService {
        return null;
    }

    /**
     * Checks whether we're running in debug mode.
     * @returns True when either debug=1 or debug=true is present as a query param, otherwise false.
     */
    public isDebug(): boolean {
        return this.isDebugEnabled;
    }

    /**
     * Override the isDebug setting. Useful when server side rendering.
     */
    public setDebug(isDebug: boolean): void {
        this.isDebugEnabled = isDebug;
    }

    /**
     * Set the tags to filter logs.
     * @param tags Tags to filter logs. If empty, do not filter.
     */
    public setTags(tags: string[]): void {
        // Not supported in console logger at the moment
    }

    /**
     * Log console message.
     * Note that these are not output to the console unless you're in debug mode.
     * @param message - Message to log.
     * @param tags - Optional tags for specifying log type for filtering logs.
     */
    public log(message: string, tags?: string[]): void {
        if (this.isDebug()) {
            console.info(message);
        }
    }

    /**
     * Log error message.
     * @param message - Message to log
     */
    public logError(message: string): void {
        console.error(message);
    }

    /**
     * Lazy log console message. Use this when the message being constructed is complex or expensive so that it's only evaluated if needed.
     * Note that the callback is not invoked and the message is not output unless you're in debug mode.
     * @param getMessage - The function to call to get message to log
     */
    public logCallback(getMessage: () => string): void  {
        if (this.isDebug()) {
            console.info(getMessage());
        }
    }

    /**
     * Log the object to the console.
     * Note that these are not output to the console unless you're in debug mode.
     * @param message - Array of messages to log
     */
    public logObjects(...message: any[]): void {
        if (this.isDebug()) {
            console.log(...message);
        }
    }
}